import { useLayoutEffect } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { prefersReducedMotion } from "../../lib/motion";

gsap.registerPlugin(ScrollTrigger);

const MotionSystem = () => {
  useLayoutEffect(() => {
    const reducedMotion = prefersReducedMotion();

    if (reducedMotion) {
      document.documentElement.classList.add("reduced-motion");
      return;
    }

    document.documentElement.classList.remove("reduced-motion");

    const context = gsap.context(() => {
      gsap.fromTo(
        "[data-hero-line] > span",
        { yPercent: 112 },
        {
          yPercent: 0,
          duration: 1.15,
          ease: "power4.out",
          stagger: 0.12,
          delay: 0.2,
        },
      );

      gsap.fromTo(
        "[data-hero-enter]",
        { opacity: 0, y: 24 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: "power3.out",
          stagger: 0.08,
          delay: 0.65,
        },
      );

      gsap
        .timeline({
          scrollTrigger: {
            trigger: "#home",
            start: "top top",
            end: "bottom top",
            scrub: 1,
          },
        })
        .to(".hero__copy", { yPercent: -14, opacity: 0.25 }, 0)
        .to(".hero__portrait-frame", { yPercent: 10, scale: 0.94 }, 0)
        .to(".hero__orbit", { rotate: 24, scale: 1.08 }, 0);

      gsap.utils.toArray<HTMLElement>("[data-reveal]").forEach((element) => {
        gsap.fromTo(
          element,
          { opacity: 0, y: 42, filter: "blur(8px)" },
          {
            opacity: 1,
            y: 0,
            filter: "blur(0px)",
            ease: "none",
            scrollTrigger: {
              trigger: element,
              start: "top 92%",
              end: "top 68%",
              scrub: 0.55,
            },
          },
        );
      });

      gsap.fromTo(
        ".experience-timeline__progress",
        { scaleY: 0 },
        {
          scaleY: 1,
          ease: "none",
          scrollTrigger: {
            trigger: ".experience-timeline",
            start: "top 70%",
            end: "bottom 62%",
            scrub: true,
          },
        },
      );

      gsap.fromTo(
        ".statement__line",
        { xPercent: -9 },
        {
          xPercent: 9,
          ease: "none",
          scrollTrigger: {
            trigger: ".statement",
            start: "top bottom",
            end: "bottom top",
            scrub: 1,
          },
        },
      );

      gsap.to(".scroll-progress__bar", {
        scaleX: 1,
        ease: "none",
        scrollTrigger: {
          start: 0,
          end: "max",
          scrub: 0.15,
        },
      });

      const media = gsap.matchMedia();

      media.add("(min-width: 960px)", () => {
        const stories = gsap.utils.toArray<HTMLElement>(
          ".project-story[data-project-index]",
        );
        const visuals = gsap.utils.toArray<HTMLElement>(
          ".project-visual[data-project-index]",
        );
        const progressCurrent = document.querySelector<HTMLElement>(
          ".project-progress__current",
        );

        const activateProject = (index: number) => {
          visuals.forEach((visual, visualIndex) => {
            const isActive = index === visualIndex;
            visual.setAttribute("aria-hidden", String(!isActive));
            visual.classList.toggle("is-active", isActive);
            gsap.to(visual, {
              autoAlpha: isActive ? 1 : 0,
              clipPath: isActive ? "inset(0% 0% 0% 0%)" : "inset(8% 0% 8% 0%)",
              scale: isActive ? 1 : 1.04,
              duration: 0.72,
              ease: "power3.out",
              overwrite: true,
            });
          });

          if (progressCurrent) {
            progressCurrent.textContent = String(index + 1).padStart(2, "0");
          }
        };

        activateProject(0);

        stories.forEach((story, index) => {
          ScrollTrigger.create({
            trigger: story,
            start: "top 54%",
            end: "bottom 54%",
            onEnter: () => activateProject(index),
            onEnterBack: () => activateProject(index),
          });

          gsap.fromTo(
            story,
            { opacity: 0.26 },
            {
              opacity: 1,
              ease: "none",
              scrollTrigger: {
                trigger: story,
                start: "top 82%",
                end: "top 54%",
                scrub: 0.4,
              },
            },
          );

          const visualImage = visuals[index]?.querySelector("img");
          if (visualImage) {
            gsap.fromTo(
              visualImage,
              { scale: 1.08 },
              {
                scale: 1,
                ease: "none",
                scrollTrigger: {
                  trigger: story,
                  start: "top bottom",
                  end: "bottom top",
                  scrub: 1,
                },
              },
            );
          }
        });

        return () => {
          gsap.set([...stories, ...visuals], { clearProps: "all" });
        };
      });
    });

    const refresh = window.setTimeout(() => ScrollTrigger.refresh(), 100);

    return () => {
      window.clearTimeout(refresh);
      context.revert();
    };
  }, []);

  return null;
};

export default MotionSystem;
